// F8 / FR-RD15: the start gate for a detected Zoom/Teams desktop meeting. Order: org policy,
// F17's shared ConsentAnnouncement (Record waits for "I've announced it" when the org mode
// requires it), then the no-system-audio warning. Presentational: RecorderPage owns the
// capture and the live session, this only decides whether it may start.
import { useState } from "react";
import { Button, Callout, Card } from "@grackle/ui";
import type { MeetingConsentMode } from "../api/types";
import ConsentAnnouncement, { announcementRequired } from "../live/ConsentAnnouncement";
import { NO_SYSTEM_AUDIO_WARNING, appLabel, startBlockedBy, type MeetingApp } from "./meetingCapture";
import "./recorder.css";

export interface MeetingStartPanelProps {
  app: MeetingApp;
  orgName?: string;
  consentMode: MeetingConsentMode;
  /** The org also set "require consent prompt" (server enforces consent_acknowledged). */
  requirePrompt?: boolean;
  orgAllows: boolean;
  systemAudioAvailable: boolean;
  /** The capture source is not ready yet (preview still priming). */
  sourceBusy?: boolean;
  /** Server refused with `desktop_meeting_capture_disabled`, or another start error. */
  error?: string | null;
  onReady: (announcedAt: string | null) => void;
  onCancel: () => void;
}

export default function MeetingStartPanel({
  app,
  orgName,
  consentMode,
  requirePrompt,
  orgAllows,
  systemAudioAvailable,
  sourceBusy,
  error,
  onReady,
  onCancel,
}: MeetingStartPanelProps) {
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [announcedAt, setAnnouncedAt] = useState<string | null>(null);
  const [audioAck, setAudioAck] = useState(false);
  const consentRequired = announcementRequired(consentMode) || Boolean(requirePrompt);

  const gate = (confirmed: boolean, ack: boolean) =>
    startBlockedBy({
      orgAllows,
      consentRequired,
      consentConfirmed: confirmed,
      systemAudioAvailable,
      noSystemAudioAcknowledged: ack,
    });

  if (!orgAllows) {
    return (
      <Callout tone="danger" icon="warning-circle">
        Desktop meeting capture is turned off for this organization — ask an organization admin to enable
        &ldquo;Allow desktop meeting capture&rdquo;.
        <div>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Back
          </Button>
        </div>
      </Callout>
    );
  }

  return (
    <div className="meeting-start">
      <Callout tone="record" icon="info">
        {appLabel(app)} meeting: Grackle records the meeting window, your microphone and — where the OS allows — the
        meeting&rsquo;s audio. The recording bar stays visible the whole time and can&rsquo;t be hidden.
      </Callout>
      {error ? (
        <Callout tone="danger" icon="warning-circle" role="alert">
          {error}
        </Callout>
      ) : null}
      {!consentConfirmed ? (
        <ConsentAnnouncement
          consentMode={consentMode}
          orgName={orgName}
          required={consentRequired}
          recordLabel="Record this meeting"
          busy={sourceBusy}
          onCancel={onCancel}
          onConfirm={(at) => {
            setAnnouncedAt(at);
            setConsentConfirmed(true);
            if (gate(true, audioAck) === null) onReady(at);
          }}
        />
      ) : gate(true, audioAck) === "system_audio_warning" ? (
        <Card size="sm">
          <Callout tone="now" icon="info" role="alert">
            {NO_SYSTEM_AUDIO_WARNING}
          </Callout>
          <div className="consent-actions">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="record"
              disabled={sourceBusy}
              onClick={() => {
                setAudioAck(true);
                onReady(announcedAt);
              }}
            >
              Record anyway
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
